// Bridge the shared mock event bus into the agent app's Zustand stores.
//
// Three responsibilities:
//   1. visitor:presence → upsert/remove rows in the visitor monitor
//   2. visitor:chat:message → ensure a Conversation exists, append to it, toast
//   3. agent:chat:message → append to the Conversation when the AI replies
//      (so agents see the AI's side of the transcript when they look in)

import { getBus, richTextToPlain } from '@prakash-software/shared';
import type {
  AgentId,
  MessageId,
  SessionId,
  SessionMedium,
  VisitorId,
  VisitorPresenceEvent,
} from '@prakash-software/shared';
import { useAgentStore } from '../stores/agent';
import { useVisitorsStore, type VisitorRow } from '../stores/visitors';
import {
  useConversationsStore,
  type Conversation,
  type ConversationMessage,
} from '../stores/conversations';
import { useNotificationsStore } from '../stores/notifications';
import { useAgentNotifications } from '../stores/agent-notifications';
import { usePageVisitsStore } from '../stores/page-visits';
import { useTypingStore } from '../stores/typing';
import { useIncomingCallsStore } from '../stores/incoming-calls';
import { usePresenceStore } from '../stores/presence';
import { useOutgoingCallsStore } from '../stores/outgoing-calls';
import { bufferOffer } from '../stores/webrtc-buffer';
import { claimOwnership, emitSystemMessage, endCallLocally } from './call-control';
import { fireBrowserNotification, tabInactive } from './pwa';
import { hydrateFromServer, resyncLiveConversations } from './hydration';
import { armToastQuietWindow } from './event-bridge';
import { visitorDisplayName } from './visitor-name';
import { isVisitorLive } from './visitor-live';
import { reportedArrival, resolveArrival } from './visitor-arrival';
import { mergeGeo, mergeIdentity } from './merge-geo';

const isLoggedIn = () => useAgentStore.getState().current !== null;

let installed = false;

export function installAgentBusBridge(): void {
  if (installed) return;
  installed = true;
  const bus = getBus();

  // ─── Connection recovery ───
  // When the bus goes 'closed' → 'open' (a fresh handshake succeeded), the
  // server doesn't replay presence — visitors aren't re-announced until
  // their next 10s heartbeat lands. To bridge the gap we force a hydration
  // pull so historical + recently-seen visitors come back from the DB,
  // and live ones overwrite as their heartbeats arrive.
  let lastConn = bus.getConnectionState();
  bus.onConnectionChange((state) => {
    const wasClosed = lastConn === 'closed' || lastConn === 'disconnected';
    lastConn = state;
    if (state === 'open') {
      // Mute "new visitor / new chat" toasts briefly: online visitors
      // re-announce via presence heartbeats over the next ~10s, and after a
      // logout→login the store was cleared so they'd all look new. Covers the
      // first connect and every reconnect.
      armToastQuietWindow();
      if (wasClosed && isLoggedIn()) {
        void hydrateFromServer(true);
        void resyncLiveConversations();
      }
    }
  });

  // ─── visitor:presence ───
  bus.on('visitor:presence', (event) => {
    upsertFromPresence(event);
    if (event.status !== 'departed') return;
    const convStore = useConversationsStore.getState();
    for (const c of convStore.byId.values()) {
      if (c.visitorId !== event.visitor.visitorId) continue;
      // Visitor went offline mid-call — tear down the agent's PC and free
      // capacity. Same cleanup path as receiving an explicit `webrtc:hangup`,
      // just labelled as 'left'.
      if (c.inVideo) {
        endCallLocally(c.sessionId, {
          reason: 'left',
          source: 'remote',
          remoteName: c.visitorName,
        });
      }
      // Transcript chip so the agent sees the visitor left. Local-only — the
      // visitor is gone, no point round-tripping it through the server. Guard
      // against a repeat 'departed' double-printing the same chip.
      const offlineText = `${c.visitorName} went offline.`;
      const last = c.messages[c.messages.length - 1];
      if (last?.author === 'system' && last.text === offlineText) continue;
      convStore.append(c.sessionId, {
        id: crypto.randomUUID() as MessageId,
        sessionId: c.sessionId,
        author: 'system',
        text: offlineText,
        sentAt: event.at,
      });
    }
  });

  // ─── visitor:page — live page-visit timeline ───
  bus.on('visitor:page', (event) => {
    usePageVisitsStore
      .getState()
      .append(event.visitorId as VisitorId, { url: event.url, at: event.at });
  });

  // ─── visitor:updated — authoritative identity change (agent edit / heal) ───
  // Keeps the monitor, the visitor-info panel, and any open conversation header
  // in sync the instant a visitor's name/email/phone changes — no refresh, and
  // it can't be reverted by a stale presence heartbeat (the server heals those).
  bus.on('visitor:updated', (event) => {
    if (!isLoggedIn()) return;
    const vid = event.visitorId as VisitorId;
    const store = useVisitorsStore.getState();
    const existing = store.rows.get(vid);
    if (existing) {
      store.patch(vid, {
        identity: {
          ...existing.identity,
          name: event.name ?? undefined,
          email: event.email ?? undefined,
          phone: event.phone ?? undefined,
        },
      });
    }
    // Reflect on any open conversation's header for this visitor.
    const convStore = useConversationsStore.getState();
    const display = visitorDisplayName(vid, event.name ?? undefined);
    for (const c of convStore.byId.values()) {
      if (c.visitorId === vid) convStore.setVisitorName(c.sessionId, display);
    }
  });

  // ─── visitor:geo — the async city resolve landed ───
  // Presence carries the country immediately (local table); the city needs an
  // external provider and arrives a moment later. Patch the monitor row in
  // place so the location fills itself in while the visitor is still browsing,
  // rather than staying a bare country until someone reloads.
  bus.on('visitor:geo', (event) => {
    if (!isLoggedIn()) return;
    const vid = event.visitorId as VisitorId;
    const store = useVisitorsStore.getState();
    const existing = store.rows.get(vid);
    if (!existing) return;
    store.patch(vid, {
      identity: {
        ...existing.identity,
        // Merge rather than replace: the resolve reports what it could place,
        // and a field it left out must not blank one presence established.
        geo: mergeGeo(existing.identity.geo, event.geo),
      },
    });
  });

  // ─── visitor:purged — an admin deleted the contact and all of its chats ───
  // The rows are gone server-side, so drop the monitor entry and close any
  // conversation for this visitor rather than leaving threads that 404 on the
  // next hydrate.
  // Notification centre: something needs this agent (a chat or ticket was
  // assigned to them, a chat reached their department). Server-only event,
  // sent to this agent's own room.
  bus.on('agent:notification', (event) => {
    if (!isLoggedIn()) return;
    useAgentNotifications.getState().add(event.notification);
    useNotificationsStore.getState().push({
      kind: 'system',
      title: event.notification.title,
      ...(event.notification.body ? { body: event.notification.body } : {}),
      ...(event.notification.link?.startsWith('/conversation/')
        ? { sessionId: event.notification.link.slice('/conversation/'.length) as SessionId }
        : {}),
    });
  });

  bus.on('visitor:purged', (event) => {
    if (!isLoggedIn()) return;
    const vid = event.visitorId as VisitorId;
    const convStore = useConversationsStore.getState();
    for (const c of [...convStore.byId.values()]) {
      if (c.visitorId === vid) convStore.close(c.sessionId);
    }
    useVisitorsStore.getState().remove(vid);
  });

  // ─── visitor:chat:message ───
  bus.on('visitor:chat:message', (event) => {
    const visitorId = event.message.authorId as VisitorId;
    const row = useVisitorsStore.getState().rows.get(visitorId);

    // Ensure a Conversation row exists. The default owner is the AI — the
    // orchestrator will reply unless a human takes over.
    const convStore = useConversationsStore.getState();
    const conv = convStore.byId.get(event.sessionId);
    const conversation: Conversation =
      conv ??
      convStore.ensure({
        sessionId: event.sessionId,
        visitorId,
        visitorName: event.message.authorName ?? visitorDisplayName(visitorId, row?.identity.name),
        medium: 'chat' as SessionMedium,
        ...(event.channel ? { channel: event.channel } : {}),
        startedBy: 'visitor',
        startedAt: event.message.sentAt,
        messages: [],
        notes: '',
        notesSaveState: 'idle',
        notesUpdatedAt: null,
        summary: null,
        summaryProvider: null,
        summaryGeneratedAt: null,
        callTranscripts: new Map(),
        inVideo: false,
        // A new visitor chat is human-side until the AI explicitly takes over
        // (server-broadcast ai:takeover flips it below). Defaulting to 'ai'
        // here mislabelled agent-first chats as AI-owned. The server decides
        // real ownership per site chat-routing; this is just the UI seed.
        ownedBy: 'agent',
      });

    const msg: ConversationMessage = {
      id: event.message.messageId,
      sessionId: event.sessionId,
      ...(event.message.sequence !== undefined ? { sequence: event.message.sequence } : {}),
      author: 'visitor',
      authorId: visitorId,
      authorName: event.message.authorName ?? row?.identity.name ?? 'Visitor',
      text: event.message.text,
      sentAt: event.message.sentAt,
      ...(event.message.attachments?.length
        ? { attachments: event.message.attachments }
        : {}),
    };
    // Skip if we already saw this messageId (e.g. self-emit + heartbeat retry).
    if (!conversation.messages.some((m) => m.id === msg.id)) {
      useConversationsStore.getState().append(event.sessionId, msg);
    }
    const me = useAgentStore.getState().current;
    if (me) {
      getBus().emit({
        type: 'message:receipt',
        sessionId: event.sessionId,
        messageId: event.message.messageId,
        status: 'delivered',
        party: 'agent',
        actorId: me.agentId,
        at: Date.now(),
      });
    }
    // A message landing means they've stopped typing — clear the indicator.
    useTypingStore.getState().setTyping(event.sessionId, false);

    // Preview: text first, else attachment summary so the visitor row reads
    // sensibly when the visitor only sent files.
    const preview =
      event.message.text ||
      (event.message.attachments?.length
        ? `📎 ${event.message.attachments[0]!.filename}${event.message.attachments.length > 1 ? ` (+${event.message.attachments.length - 1})` : ''}`
        : '');
    useVisitorsStore.getState().noteIncoming(visitorId, event.message.sentAt, preview);

    // When the agent isn't on the tab, raise an OS notification for the
    // incoming message (the in-app toast only fires for the FIRST message via
    // event-bridge; follow-ups would otherwise go unseen while backgrounded).
    // Click focuses the tab + opens the conversation.
    if (isLoggedIn() && tabInactive()) {
      fireBrowserNotification(conversation.visitorName, {
        body: preview || 'New message',
        sessionId: event.sessionId,
        tag: event.sessionId,
      });
    }

    // NOTE: the "incoming chat" toast is raised by event-bridge.ts when the
    // conversation row first appears (it carries the sessionId + display name
    // for click-to-open). We deliberately DON'T toast here too — doing both
    // produced two toasts per chat, so dismissing one left its twin behind.
  });

  // ─── visitor:typing → ephemeral "visitor is typing…" indicator ───
  const typingTimers = new Map<string, number>();
  bus.on('visitor:typing', (event) => {
    useTypingStore.getState().setTyping(event.sessionId, event.isTyping);
    const prev = typingTimers.get(event.sessionId);
    if (prev) window.clearTimeout(prev);
    if (event.isTyping) {
      // Safety auto-clear in case a trailing `false` gets dropped.
      typingTimers.set(
        event.sessionId,
        window.setTimeout(() => {
          useTypingStore.getState().setTyping(event.sessionId, false);
          typingTimers.delete(event.sessionId);
        }, 6000),
      );
    } else {
      typingTimers.delete(event.sessionId);
    }
  });

  bus.on('message:receipt', (event) => {
    useConversationsStore.getState().markMessageReceipt(
      event.sessionId,
      event.messageId,
      event.status,
      event.at,
    );
  });

  // A channel reply moved through delivery (sent / read / failed / retried).
  bus.on('message:delivery', (event) => {
    useConversationsStore.getState().setDeliveryStatus(event.sessionId, event.messageId, event.status, event.error);
  });

  // ─── visitor:chat:request — chat-without-message (e.g. visitor clicked Start) ───
  bus.on('visitor:chat:request', (event) => {
    if (!isLoggedIn()) return;
    const row = useVisitorsStore.getState().rows.get(event.visitorId);
    useNotificationsStore.getState().push({
      kind: 'chat',
      title: row?.identity.name ?? 'Visitor wants to chat',
      body: event.firstMessage?.slice(0, 60) ?? 'Take the conversation from the monitor.',
    });
  });

  // ─── visitor:chat:escalate (to video) — Phase 3.3 will route properly ───
  bus.on('visitor:chat:escalate', () => {
    if (!isLoggedIn()) return;
    useNotificationsStore.getState().push({
      kind: 'escalate',
      title: 'Visitor wants video',
      body: 'Existing chat is requesting a video call',
    });
  });

  // ─── system:message — ambient transcript annotations from the OTHER
  // side ("You ended the call · 02:14" from visitor → agent). Server
  // persists, so this restores them on hydration too. Dedupe on
  // messageId because we already self-appended when we emitted our own.
  bus.on('system:message', (event) => {
    const conv = useConversationsStore.getState().byId.get(event.sessionId);
    if (!conv) return;
    if (conv.messages.some((m) => m.id === event.messageId)) return;
    useConversationsStore.getState().append(event.sessionId, {
      id: event.messageId,
      sessionId: event.sessionId,
      ...(event.sequence !== undefined ? { sequence: event.sequence } : {}),
      author: 'system',
      text: event.text,
      sentAt: event.at,
    });
  });

  bus.on('ai:response:preview', (event) => {
    useConversationsStore.getState().setAiPreview(event.sessionId, { id: event.messageId, text: event.text, revision: event.revision });
  });
  bus.on('ai:response:clear', (event) => {
    if (useConversationsStore.getState().byId.get(event.sessionId)?.aiPreview?.id === event.messageId) useConversationsStore.getState().setAiPreview(event.sessionId, null);
  });

  // ─── agent:chat:message — mirror AI replies into the Conversation log ───
  bus.on('agent:chat:message', (event) => {
    const conv = useConversationsStore.getState().byId.get(event.sessionId);
    if (!conv) return; // no conv = nothing to attach to

    // Update the visitor row so the monitor surfaces "AI replied" / "agent
    // replied" in the preview and the row sorts to the top. Doesn't bump
    // unread — only visitor messages do that.
    // The monitor preview is a one-liner — flatten the AI's markdown so the
    // row reads "Book a call at calendly…", not "**Book a call** at [x](y)".
    const preview = event.message.richText?.length
      ? richTextToPlain(event.message.richText)
      : event.message.text;
    useVisitorsStore.getState().noteIncoming(
      conv.visitorId,
      event.message.sentAt,
      preview.slice(0, 80),
      event.message.author === 'ai' ? 'ai' : 'agent',
    );

    if (event.message.author !== 'ai') {
      // Human agent's message — mark this session as owned by that agent so
      // other agents/admin viewing the monitor see "chat · <Agent>" cross-
      // session. Local owner-of-self gets the same write (no-op).
      if (event.message.authorId) {
        useConversationsStore
          .getState()
          .setOwner(event.sessionId, 'agent', event.message.authorId as AgentId);
      }
      // Human agent messages were already appended locally by the sender;
      // don't double-insert into the conversation log.
      return;
    }
    if (conv.messages.some((m) => m.id === event.message.messageId)) return;
    useConversationsStore.getState().append(event.sessionId, {
      id: event.message.messageId,
      sessionId: event.sessionId,
      ...(event.message.sequence !== undefined ? { sequence: event.message.sequence } : {}),
      author: 'ai',
      authorName: event.message.authorName ?? 'AI Assistant',
      text: event.message.text,
      ...(event.message.richText?.length ? { richText: event.message.richText } : {}),
      ...(event.message.quickReplies?.length ? { quickReplies: event.message.quickReplies } : {}),
      ...(event.message.citations?.length ? { citations: event.message.citations } : {}),
      sentAt: event.message.sentAt,
    });
  });

  // ─── agent:status:in-call — propagate video state across agents ───
  // The agent who owns a video call broadcasts this. Other agents/admin
  // viewing the monitor read it to render "video · <agent>" on the right row.
  bus.on('agent:status:in-call', (event) => {
    if (!isLoggedIn()) return;
    const me = useAgentStore.getState().current;
    if (me && event.agentId === me.agentId) return; // we already wrote local
    if (!event.videoSessionId) return;
    const conv = useConversationsStore
      .getState()
      .byId.get(event.videoSessionId);
    if (!conv) return;
    useConversationsStore
      .getState()
      .setVideo(event.videoSessionId, event.isInCall);
    useConversationsStore
      .getState()
      .setOwner(event.videoSessionId, 'agent', event.agentId);
  });

  // ─── agent:presence — live "who's online" roster (server-broadcast) ───
  // The server announces each agent online/offline (and replays the current
  // roster to us on connect). We keep everyone EXCEPT ourselves in the
  // presence store; the header renders our own avatar from the agent store.
  bus.on('agent:presence', (event) => {
    const me = useAgentStore.getState().current;
    if (me && event.agentId === me.agentId) return; // self is rendered separately
    const store = usePresenceStore.getState();
    // Presence is per SITE ROOM, so both directions name one site, not the
    // agent's whole set. Going offline on one site must not evict an agent who
    // is still staffing another.
    if (event.status === 'offline') {
      store.removeFromSite(event.agentId, event.siteId);
      return;
    }
    store.upsert({
      agentId: event.agentId,
      name: event.name ?? 'Agent',
      initials: event.initials ?? '?',
      availability: event.availability ?? 'available',
      ...(event.role ? { role: event.role } : {}),
      ...(event.avatarUrl ? { avatarUrl: event.avatarUrl } : {}),
      siteIds: new Set([event.siteId]),
      chatsInUse: 0,
      chatCap: 0,
      onVideo: false,
    });
  });

  // ─── webrtc:hangup — clean up local PC/video state + cancel pending invites ───
  bus.on('webrtc:hangup', (event) => {
    if (!isLoggedIn()) return;
    const me = useAgentStore.getState().current;
    if (!me) return;
    if (event.from === me.agentId) return; // our own echo
    const conv = useConversationsStore.getState().byId.get(event.sessionId);
    const remoteName = conv?.visitorName;
    endCallLocally(event.sessionId, {
      reason: event.reason ?? 'ended',
      source: 'remote',
      ...(remoteName ? { remoteName } : {}),
    });
    // Also clear any pending banner for this session (caller cancelled).
    useIncomingCallsStore.getState().drop(event.sessionId);
  });

  // ─── webrtc:offer — buffer for the Conversation route to pick up ───
  // Solves the accept→navigate→mount race; watchForOffer reads from the
  // buffer on mount and then keeps subscribing for fresh offers.
  bus.on('webrtc:offer', (event) => {
    if (!isLoggedIn()) return;
    bufferOffer(event.sessionId, event.sdp);
  });

  // ─── webrtc:invite — pop the incoming-call banner ───
  bus.on('webrtc:invite', (event) => {
    if (!isLoggedIn()) return;
    const me = useAgentStore.getState().current;
    if (!me) return;
    // Ring if I'm the explicit target, OR the invite is undirected (visitor
    // didn't know which agent to address — `to === from` placeholder).
    const isMine = event.to === me.agentId;
    const isUndirected = event.to === event.from;
    if (!isMine && !isUndirected) return;

    // If I'm already on a call, I can't accept this one. For directed
    // invites: auto-reject so the caller isn't left waiting. For undirected:
    // stay silent — another agent will hopefully pick up.
    const alreadyOnCall = [...useConversationsStore.getState().byId.values()].some(
      (c) => c.inVideo && c.ownerAgentId === me.agentId,
    );
    if (alreadyOnCall) {
      if (isMine) {
        try {
          bus.emit({
            type: 'webrtc:invite:response',
            sessionId: event.sessionId,
            from: me.agentId,
            to: event.from,
            response: 'reject',
            at: Date.now(),
          });
        } catch {
          /* fire-and-forget */
        }
      }
      return;
    }

    const conv = useConversationsStore.getState().byId.get(event.sessionId);
    const fromName =
      event.fromName ?? conv?.visitorName ?? 'Visitor';
    useIncomingCallsStore.getState().push({
      sessionId: event.sessionId,
      from: event.from,
      fromName,
      to: event.to,
      at: event.at,
    });
  });

  // ─── webrtc:invite:response — handle response to MY outgoing invite ───
  bus.on('webrtc:invite:response', (event) => {
    if (!isLoggedIn()) return;
    const me = useAgentStore.getState().current;
    if (!me) return;

    // Clear any LOCAL pending incoming banner for this session — another
    // agent already responded to the visitor's invite, no need to keep
    // ringing on this one.
    useIncomingCallsStore.getState().drop(event.sessionId);

    if (event.to !== me.agentId) return;

    const out = useOutgoingCallsStore.getState();
    const pending = out.pending.get(event.sessionId);
    if (!pending) return; // We cancelled locally; ignore stale response.

    out.drop(event.sessionId);
    if (event.response === 'accept') {
      // Become the caller for this session — Conversation.tsx will start the
      // offer when inVideo flips. Also claim ownership (this WAS our invite,
      // so we're definitively taking over from whoever — AI or no-one).
      out.markCaller(event.sessionId);
      useConversationsStore.getState().setVideo(event.sessionId, true);
      claimOwnership(event.sessionId, 'visitor-accepted-call');
    } else {
      const conv = useConversationsStore.getState().byId.get(event.sessionId);
      if (conv) {
        emitSystemMessage(
          event.sessionId,
          `${conv.visitorName} declined the video call.`,
          event.at,
        );
      }
    }
  });

  // ─── ai:escalate → another agent claimed ownership; reflect locally ───
  // The agent app emits this when accepting a call or taking over; other
  // agents/admin need to see "chat · <agent>" / "video · <agent>" change.
  // We can't know which agent for `agent-now-available` (the schema doesn't
  // carry an AgentId), so we flip ownedBy to 'agent' without setting an id.
  // The agent who did the takeover writes their own id locally already.
  bus.on('ai:escalate', (event) => {
    if (!isLoggedIn()) return;
    if (event.trigger !== 'agent-now-available') return;
    const conv = useConversationsStore.getState().byId.get(event.sessionId);
    if (!conv) return;
    if (conv.ownedBy === 'agent' && conv.ownerAgentId) return; // already agent-owned
    useConversationsStore.getState().setOwner(event.sessionId, 'agent');
  });

  // ─── ai:takeover ───
  // The system chip ("AI assistant took over (…)") is emitted by the
  // SERVER orchestrator now (one persisted message per session), so the
  // agent app no longer re-emits it. We DO flip the conversation to AI-owned
  // so the header shows "Take over" (not "Hand off") — this is the
  // authoritative signal that the AI is driving, replacing the old
  // assume-AI-on-create default.
  bus.on('ai:takeover', (event) => {
    const conv = useConversationsStore.getState().byId.get(event.sessionId);
    if (!conv || conv.ownedBy === 'ai') return;
    useConversationsStore.getState().setOwner(event.sessionId, 'ai');
  });
}

function upsertFromPresence(event: VisitorPresenceEvent) {
  const store = useVisitorsStore.getState();
  const existing = store.rows.get(event.visitor.visitorId);
  const now = event.at;

  // `arrivedAt` drives the default (newest-first) monitor ordering and must be
  // STABLE while a visitor stays continuously present — heartbeats, messages
  // and status-label changes never reshuffle the list. But a visitor who was
  // OFFLINE (departed, or seeded from history/rehydration, or whose heartbeat
  // lapsed past the live grace window) and now reappears is a genuinely fresh
  // arrival: re-stamp arrivedAt so they rise to the top instead of staying
  // buried under an old first-seen timestamp. `existing` alive at event time →
  // keep it; otherwise treat this presence as a new arrival.
  const isArrival = !existing || !isVisitorLive(existing, now);

  // When the visitor actually landed. The widget reports the elapsed time of
  // the visitor's current visit, so this is "here for 4 minutes" rather than
  // "first seen whenever this agent happened to log in". See
  // lib/visitor-arrival.ts for the clock and drift reasoning.
  const claimed = reportedArrival(now, event.context.timeOnSite);
  const arrivedAtFor = (held: number | null): number =>
    resolveArrival(held, claimed, now, {
      departed: event.status === 'departed',
      freshArrival: isArrival,
    });

  // We used to REMOVE the row on `departed`. That left the agent without
  // context if they had an open conversation with that visitor. We now keep
  // the row, marked offline, so the monitor + open conversation can both
  // show that the visitor is gone (and disable composing replies into the
  // dark).
  const row: VisitorRow = existing
    ? {
        ...existing,
        // mergeIdentity, NOT a spread: `geo` is an object, and spreading the
        // presence payload over it replaced the city with the country-only
        // view every heartbeat carries. See lib/merge-geo.ts.
        identity: mergeIdentity(existing.identity, event.visitor),
        context: { ...existing.context, ...event.context },
        status: event.status,
        siteId: event.siteId ?? existing.siteId,
        seenAt: now,
        arrivedAt: arrivedAtFor(existing.arrivedAt),
      }
    : {
        identity: event.visitor,
        context: event.context,
        status: event.status,
        siteId: event.siteId,
        seenAt: now,
        arrivedAt: arrivedAtFor(null),
        lock: null,
        unread: 0,
      };
  store.upsert(row);
}
